import { useIntl } from "react-intl";
import { downloadBudgetAttachmentV2 } from "../../../../api/fetchBudgetV2";
import { IBudgetAttachmentFile } from "./types";
import {
  formatBudgetAttachmentSize,
  getBudgetAttachmentPreviewUrl,
  getBudgetAttachmentThumbnail,
  isImageBudgetAttachment,
  isPdfBudgetAttachment,
  isSpreadsheetBudgetAttachment,
} from "./utils";

interface IBudgetAttachmentCardProps {
  file: IBudgetAttachmentFile;
  budgetId?: string;
  allowRemove?: boolean;
  onRemove?: () => void;
}

export const BudgetAttachmentCard = ({
  file,
  budgetId,
  allowRemove = false,
  onRemove,
}: IBudgetAttachmentCardProps) => {
  const intl = useIntl();
  const previewUrl = getBudgetAttachmentPreviewUrl(file);
  const isImage = isImageBudgetAttachment(file);

  const handleDownload = async () => {
    if (file.link) {
      const link = document.createElement("a");
      link.href = file.link;
      link.setAttribute("download", file.name || "");
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      return;
    }

    if (file.file) {
      const url = URL.createObjectURL(file.file);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name || "");
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    if (!budgetId || !file.name) return;

    try {
      const response = await downloadBudgetAttachmentV2(budgetId, file.name);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[BudgetAttachmentCard] Erro ao baixar arquivo:", error);
    }
  };

  return (
    <div className="border border-gray-200 rounded-md p-4 hover:bg-gray-50 transition-colors">
      <div className="flex">
        <div className="thumbnail mr-4 flex justify-center items-center w-[200px] h-[120px] border border-gray-200 rounded bg-gray-50 relative overflow-hidden flex-shrink-0">
          {isPdfBudgetAttachment(file) && previewUrl ? (
            <div className="w-full h-full relative">
              <iframe
                src={`${previewUrl}#page=1&zoom=page-fit`}
                className="w-full h-full border-0"
                title={file.name}
                style={{ pointerEvents: "none" }}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-size-10 px-2 py-1 flex items-center justify-center">
                <span>PDF</span>
              </div>
            </div>
          ) : isPdfBudgetAttachment(file) ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-red-100 p-4">
              <div className="text-size-12 text-gray-700 text-center font-semibold">PDF</div>
              <div className="text-size-10 text-gray-600 text-center mt-1 truncate w-full">{file.name}</div>
            </div>
          ) : isSpreadsheetBudgetAttachment(file) ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
              <div className="text-size-10 text-gray-600 text-center font-semibold">
                {file.name?.toLowerCase().endsWith(".csv")
                  ? "CSV"
                  : intl.formatMessage({ id: "intl-msg:budgets.attachments.spreadsheet" })}
              </div>
            </div>
          ) : (
            <img
              src={getBudgetAttachmentThumbnail(file)}
              alt={file.name}
              style={{
                width: "200px",
                height: isImage ? "120px" : "65px",
                objectFit: isImage ? "cover" : "contain",
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/assets/images/file-icon.svg";
              }}
            />
          )}
        </div>
        <div className="flex justify-between w-full">
          <div className="flex-1">
            <div className="font-bold mb-1">{file.name}</div>
            {(file.type || file.file?.type) && (
              <div className="text-size-12 text-gray-600">
                {intl.formatMessage({ id: "intl-msg:budgets.attachments.type" })}: {file.type || file.file?.type}
              </div>
            )}
            {file.size !== undefined && file.size !== null && file.size !== "" && (
              <div className="text-size-12 text-gray-600">
                {intl.formatMessage({ id: "intl-msg:budgets.attachments.size" })}:{" "}
                {formatBudgetAttachmentSize(
                  file.size,
                  intl.formatMessage({ id: "intl-msg:budgets.attachments.sizeUnavailable" })
                )}
              </div>
            )}
            {file.updatedAt && (
              <div className="text-size-12 text-gray-600">
                {intl.formatMessage({ id: "intl-msg:budgets.attachments.lastModified" })}:{" "}
                {intl.formatDate(file.updatedAt, { day: "2-digit", month: "2-digit", year: "numeric" })}
              </div>
            )}
            <div className="text-size-12 text-gray-600">
              {intl.formatMessage({ id: "intl-msg:budgets.attachments.path" })}: {file.name}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="cursor-pointer bg-transparent border-none p-2 hover:bg-gray-200 rounded transition-colors"
              onClick={handleDownload}
              title={intl.formatMessage({ id: "intl-msg:budgets.attachments.download" })}
            >
              <img
                src="/assets/images/downloadFile.svg"
                alt={intl.formatMessage({ id: "intl-msg:budgets.attachments.download" })}
                className="w-6 h-6"
              />
            </button>
            {allowRemove && (
              <button
                type="button"
                className="cursor-pointer bg-transparent border-none p-2 hover:bg-gray-200 rounded transition-colors"
                onClick={onRemove}
                title={intl.formatMessage({ id: "intl-msg:budgets.attachments.remove" })}
              >
                <img
                  src="/assets/images/bin.svg"
                  alt={intl.formatMessage({ id: "intl-msg:budgets.attachments.remove" })}
                  className="w-6 h-6"
                />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
